import type { DocsModelCapability, DocsModelMatch, DocsModelSelector } from '@nadoutong/contracts';
import type { DocsModelValues } from './placeholders.ts';

/**
 * The only catalog facts the docs renderer reads. Deliberately not
 * `CatalogResponse` — billing, fx, and vendor chrome are none of this
 * page's business, and a narrower slice keeps the tests from minting a
 * full SKU row.
 */
export interface DocsCatalogSlice {
  readonly skus?: readonly {
    readonly vendor: string;
    readonly sku_name: string;
  }[];
}

/**
 * Resolve every `model_selectors` entry against the live catalog.
 * `{{model:x}}` takes the first match (or the selector's fallback);
 * `{{models:x}}` joins every match with ` / ` (or the fallback).
 *
 * A missing catalog is treated as "no match" for substitution — the
 * fallback literal still resolves the token. Visibility
 * (`capability_available`) is a different question: see
 * {@link docsAvailableCapabilities}.
 */
export function resolveDocsModelValues(
  selectors: readonly DocsModelSelector[],
  catalog: DocsCatalogSlice | undefined,
): DocsModelValues {
  const one: Partial<Record<DocsModelCapability, string>> = {};
  const many: Partial<Record<DocsModelCapability, string>> = {};
  for (const selector of selectors) {
    const names = matchSkuNames(selector.match, catalog);
    const first = names[0];
    one[selector.capability] = first === undefined ? selector.fallback : first;
    many[selector.capability] = names.length > 0 ? names.join(' / ') : selector.fallback;
  }
  return { one, many };
}

/**
 * Capabilities the catalog actually resolved. `null` means the catalog
 * is not in a state we can answer from (still loading, failed, or a
 * legacy body with no `skus[]`) — the visibility walker then returns
 * `'unknown'`, which the top-level rule collapses to visible.
 */
export function docsAvailableCapabilities(
  selectors: readonly DocsModelSelector[],
  catalog: DocsCatalogSlice | undefined,
): ReadonlySet<DocsModelCapability> | null {
  if (catalog?.skus === undefined) return null;
  const available = new Set<DocsModelCapability>();
  for (const selector of selectors) {
    if (matchSkuNames(selector.match, catalog).length > 0) {
      available.add(selector.capability);
    }
  }
  return available;
}

function matchSkuNames(
  match: DocsModelMatch,
  catalog: DocsCatalogSlice | undefined,
): readonly string[] {
  if (match.by === 'none') return [];
  const skus = catalog?.skus;
  if (skus === undefined) return [];
  switch (match.by) {
    case 'vendor':
      return skuNames(skus.filter((sku) => sku.vendor === match.vendor));
    case 'vendor_name_contains': {
      const needle = match.needle.toLowerCase();
      return skuNames(
        skus.filter(
          (sku) => sku.vendor === match.vendor && sku.sku_name.toLowerCase().includes(needle),
        ),
      );
    }
    case 'name_contains': {
      const needle = match.needle.toLowerCase();
      return skuNames(skus.filter((sku) => sku.sku_name.toLowerCase().includes(needle)));
    }
    default:
      return [];
  }
}

function skuNames(skus: readonly { readonly sku_name: string }[]): readonly string[] {
  return skus.map((sku) => sku.sku_name);
}
